import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output} from '@angular/core';
import {FormControl, Validators} from "@angular/forms";
import {debounceTime, filter, first, switchMap, tap} from "rxjs/operators";
import {WebsiteService} from "../../../../../../../websites/website.service";
import {DbValidators} from "../../../../../../../shared/validators/DbValidators";
import {ActivatedRoute, Router} from "@angular/router";
import {Location} from "@angular/common";
import {GoogleAnalyticsService} from "../../../../../../../shared/services/google-analytics/google-analytics.service";
import * as fromStore from "../../../../../../../store";
import {UserService} from "../../../../../../../user/user.service";
import {Store} from "@ngrx/store";

@Component({
    selector: 'db-create-ai-steps',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './create-ai-steps.component.html',
    styleUrls: ['./create-ai-steps.component.scss'],
    standalone: false
})
export class CreateAiStepsComponent implements OnInit {

  @Output() onProceed: EventEmitter<string> = new EventEmitter<string>();

  public recreateUrl: FormControl = new FormControl('', [Validators.required, DbValidators.trimStart , Validators.pattern('https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,15}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)')]);

  public urlChecking = false;
  public isMobile: boolean;
  public fromConvert: boolean;
  public title: string = 'text_aiCreateTitle';
  public desc: string = 'text_aiCreateDesc';
  public showDomainInput: string = '';
  public desiredDomainName: string = '';
  public userLocalStorage: any;

  constructor(
    private _websiteService: WebsiteService,
    private _cdRef: ChangeDetectorRef,
    private _location: Location,
    private _router: Router,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _route: ActivatedRoute,
    private _userService: UserService,
    private _store: Store<fromStore.DbStoreState>,
  ) {
  }

  ngOnInit() {
    const queryParams: any = (<any>this._route.queryParams).value;
    this.fromConvert = !!queryParams.from_convert;
    const queryParamsIndex: number = this._router.url.indexOf('?');
    if (queryParamsIndex !== -1) {
      this._location.replaceState(this._router.url.substring(0, queryParamsIndex));
    }
    if (this.fromConvert) {
      this.title = 'text_aiConvertTitle';
      this.desc = 'text_aiConvertDesc';
    }
    if (window.innerWidth < 991) {
      this.isMobile = true;
    }
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getUserLocalStorage)),
        first()
      )
      .subscribe((userLocalStorage: any) => {
        this.userLocalStorage = userLocalStorage;
      });
    this._handleUrlChange();
  }

  public _handleUrlChange(): void {
    this.recreateUrl.valueChanges
      .pipe(
        tap(() => {
          this.urlChecking = this.recreateUrl.valid;
          this._cdRef.markForCheck();
        }),
        debounceTime(500),
      )
      .subscribe(() => this._checkUrl());
  }

  public proceedAICreation(): void {
    if (this.recreateUrl.invalid || this.urlChecking) return;
    this._onboardingDomainName();
    this.onProceed.emit(this.recreateUrl.value);
  }

  private _checkUrl(): void {
    if (this.recreateUrl.invalid) return;
    this._websiteService.checkUrl(this.recreateUrl.value)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(response => {
        this.urlChecking = false;
        this.recreateUrl.markAsTouched();
        if (response && (!response.success || !response.data.url_is_valid)) {
          this.recreateUrl.setErrors({invalidUrl: 'This site can’t be reached. please change URL.'});
        }
        this._cdRef.markForCheck();
      });
  }
  public domainShow(answer): void {
    this.showDomainInput = answer;
    this._sendEvent('Dashboard Action', this.showDomainInput, 'Convert a Website with AI Builder - Do you own a domain');
  }

  private _sendEvent(category: string, label: string, action = 'Convert a Website with AI Builder - Question answered'): void {
    let analyticsData = {
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  private _onboardingDomainName(): void {
      this._generateDomainInfo(this.desiredDomainName);
  }

  private _generateDomainInfo(domainName): void {
    const domainInfo = JSON.stringify({
      "want_domain": this.showDomainInput === 'no' ? 1 : 0,
      "domain_name": domainName || ""
    });
    localStorage.setItem('dbOnboardingDomainInfo', domainInfo);
    this.userLocalStorage['suggestedDomainNameInitial'] = localStorage.getItem('dbOnboardingDomainNameInitial');
    this.userLocalStorage['suggestedDomainName'] = domainName;
    this._updateLocalStorage();
  }

  private _updateLocalStorage(): void {
    this._userService.setToLocal(this.userLocalStorage)
      .pipe(
        first(response => !!response, null),
      )
      .subscribe(response => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadUserLocalStorage());
          this._store.dispatch(new fromStore.LoadUser());
        }
      });
  }

}
